import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { type MenuItem } from '@databases/contextual-menu/ContextualMenu';
import { SummationIcon } from '@databases/icons';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { type DatabaseField } from '@databases/types/database';
import { type DatabaseCalculationType } from '@databases/types/database/view';
import { getFieldCalculationType } from '@databases/utils/database/utils';
import useView from '@databases/views/useView';
import React from 'react';
import { useIntl } from 'react-intl-next';

import { DatabaseCalculationLabel } from './DatabaseCalculationLabel';
import {
  getCalculationOptionsForFieldType,
  handleCalculationSelectionChange,
  isQuickCalcDisabledFieldTypes,
} from './quickCalcUtils';

const QUICK_CALC_MENU_ITEM_KEY = 'quick_calculation';
const QUICK_CALC_NONE_ITEM_KEY = 'quick-calc-none';

type UseQuickCalcMenuItemProps = {
  field: DatabaseField;
  contentId: string;
  canEditFields: boolean;
  onSelect?: () => void;
};

/**
 * Returns a MenuItem that can be rendered within a contextual menu.
 * When the field type supports calculations, the item includes a submenu
 * with calculation options. Otherwise, the item is disabled.
 *
 * Returns `null` when the user doesn't have edit permissions.
 */
export const useQuickCalcMenuItem = ({
  field,
  contentId,
  canEditFields,
  onSelect,
}: UseQuickCalcMenuItemProps): MenuItem | null => {
  const { formatMessage } = useIntl();
  const { view } = useView();
  const analyticsClient = useAnalytics();
  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();

  if (!canEditFields) {
    return null;
  }

  const isDisabled = isQuickCalcDisabledFieldTypes(field.type);
  const currentCalculationType = getFieldCalculationType(
    field.id,
    view.settings.calculations
  );

  const buildSubmenuItems = (): MenuItem[] => {
    const handleSelect = (calc?: DatabaseCalculationType) => {
      handleCalculationSelectionChange(
        calc ? { value: calc, label: calc } : null,
        contentId,
        dispatch,
        field.id,
        view.id
      );

      const analyticsBase = {
        databaseContainer: getDatabaseContainerType(),
        fieldId: field.id,
        fieldType: field.type,
        source: 'quickCalcFieldActions' as const,
        contentId,
      };

      if (!calc) {
        analyticsClient.sendRemoveCalculationEvent(analyticsBase);
      } else if (currentCalculationType && currentCalculationType !== calc) {
        analyticsClient.sendCalculationEventConverted({
          ...analyticsBase,
          fromCalculationType: currentCalculationType,
          toCalculationType: calc,
        });
      } else if (!currentCalculationType) {
        analyticsClient.sendCreateCalculationEvent({
          ...analyticsBase,
          calculationType: calc,
        });
      }

      onSelect?.();
    };

    const options = getCalculationOptionsForFieldType(
      field,
      store.getState(),
      contentId,
      dispatch
    );

    const items: MenuItem[] = options.map((calc) => ({
      key: `quick-calc-${calc}`,
      content: <DatabaseCalculationLabel type={calc} fieldType={field.type} />,
      isChecked: currentCalculationType === calc,
      closeOnClick: true,
      onClick: () => handleSelect(calc),
    }));

    if (currentCalculationType) {
      items.unshift({
        key: QUICK_CALC_NONE_ITEM_KEY,
        content: formatMessage(messages['quick.calculations.calculation-type-none']),
        isChecked: false,
        closeOnClick: true,
        onClick: () => handleSelect(undefined),
      });
    }

    return items;
  };

  const quickCalcMenuItem: MenuItem = {
    key: QUICK_CALC_MENU_ITEM_KEY,
    name: 'quick-calc',
    content: formatMessage(messages['quick.calculations.placeholder-text']),
    elemBefore: <SummationIcon label="" />,
    isDisabled,
    tooltipDescription: isDisabled
      ? formatMessage(messages['quick.calculations.field-disabled-text'])
      : undefined,
    elemAfter: isDisabled ? <StatusInformationIcon label="" /> : undefined,
  };

  if (isDisabled) {
    return quickCalcMenuItem;
  }

  return { ...quickCalcMenuItem, submenuItems: buildSubmenuItems() };
};
